import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { 
  FaEye, 
  FaSearch, 
  FaFilter, 
  FaTicketAlt,
  FaCheckCircle,
  FaClock
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Tickets.css';
import Pagination from '../Common/Pagination';

const TicketsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allTickets, setAllTickets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'customer', 'provider'

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get(ENDPOINTS.SUPPORT.ADMIN_ALL);
      const data = Array.isArray(res) ? res : (res.data || []);
      setAllTickets(data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      if (error.response && error.response.status === 404) {
         console.warn("API Endpoint not found. Did you rebuild the backend?");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    let filteredData = [...allTickets];
    
    if (statusFilter !== 'all') {
      filteredData = filteredData.filter(t => t.status && t.status.toLowerCase() === statusFilter.toLowerCase());
    }
    
    if (roleFilter === 'customer') {
      filteredData = filteredData.filter(t => t.userRole === 'customer' || t.userRole === 'parent');
    } else if (roleFilter === 'provider') {
      filteredData = filteredData.filter(t => t.userRole === 'provider' || t.userRole === 'caregiver');
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredData = filteredData.filter(t => {
        const pName = `${t.provider?.firstName || ''} ${t.provider?.lastName || ''}`.trim().toLowerCase();
        const cName = `${t.customer?.firstName || ''} ${t.customer?.lastName || ''}`.trim().toLowerCase();
        return (
          (t.ticketId && t.ticketId.toLowerCase().includes(term)) || 
          (t.subject && t.subject.toLowerCase().includes(term)) ||
          (pName && pName.includes(term)) ||
          (cName && cName.includes(term))
        );
      });
    }
    
    setPagination(prev => ({ ...prev, total: filteredData.length }));
    
    const start = (pagination.page - 1) * pagination.limit;
    const paginatedData = filteredData.slice(start, start + pagination.limit);
    
    setTickets(paginatedData);
  }, [allTickets, statusFilter, searchTerm, roleFilter, pagination.page, pagination.limit]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'open': { class: 'badge-danger', icon: <FaTicketAlt />, label: 'Open' },
      'in-progress': { class: 'badge-warning', icon: <FaClock />, label: 'In Progress' },
      'resolved': { class: 'badge-success', icon: <FaCheckCircle />, label: 'Resolved' },
      'closed': { class: 'badge-secondary', icon: <FaCheckCircle />, label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig['open'];
    return (
      <span className={`badge ${config.class}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'high': { class: 'priority-high', label: 'High' },
      'medium': { class: 'priority-medium', label: 'Medium' },
      'low': { class: 'priority-low', label: 'Low' }
    };
    const config = priorityConfig[priority] || priorityConfig['medium'];
    return <span className={`priority-badge ${config.class}`}>{config.label}</span>;
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Fetching tickets...</p>
      </div>
    );
  }

  return (
    <div className="tickets-page">
      <div className="page-header">
        <div>
          <h1>Support Tickets</h1>
          <p className="page-subtitle">Manage customer and provider questions, issues, and requests</p>
        </div>
      </div>
      
      {/* Role Tabs */}
      <div className="role-tabs mb-4 d-flex gap-2">
        <button 
          className={`btn ${roleFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => { setRoleFilter('all'); setPagination(prev => ({ ...prev, page: 1 })); }}
        >
          All Tickets
        </button>
        <button 
          className={`btn ${roleFilter === 'customer' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => { setRoleFilter('customer'); setPagination(prev => ({ ...prev, page: 1 })); }}
        >
          Customer Tickets
        </button>
        <button 
          className={`btn ${roleFilter === 'provider' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => { setRoleFilter('provider'); setPagination(prev => ({ ...prev, page: 1 })); }}
        >
          Provider Tickets
        </button>
      </div>

      <div className="content-card filters-card">
        <div className="filters-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by ID, Subject, or Name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="search-input"
                style={{ width: '100%', paddingRight: '30px' }}
              />
              {searchTerm && (
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{ position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                  title="Clear search"
                >
                  &#x2715;
                </button>
              )}
            </div>
          </div>

          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h3>All Tickets ({pagination.total})</h3>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle custom-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Sender</th>
                <th>Role</th>
                <th>Subject</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length > 0 ? tickets.map((ticket) => (
                <tr 
                  key={ticket.id}
                  onClick={() => navigate(`/support/tickets/${ticket.id}`)}
                  className="ticket-row-clickable"
                  style={{ cursor: 'pointer' }}
                >

                  <td>
                    <span className="ticket-id">{ticket.ticketId}</span>
                  </td>
                  <td>
                    {ticket.userRole === 'caregiver' || ticket.userRole === 'provider'
                      ? `${ticket.provider?.firstName || ''} ${ticket.provider?.lastName || ''}`.trim() || 'Provider'
                      : `${ticket.customer?.firstName || ''} ${ticket.customer?.lastName || ''}`.trim() || 'Customer'}
                  </td>
                  <td>
                    <span className={`role-badge ${ticket.userRole}`}>
                      {ticket.userRole}
                    </span>
                  </td>
                  <td>
                    <div className="truncate-text" style={{ maxWidth: 200 }} title={ticket.subject}>
                      {ticket.subject}
                    </div>
                  </td>
                  <td>{getPriorityBadge(ticket.priority)}</td>
                  <td>{getStatusBadge(ticket.status)}</td>
                  <td>
                    <span className="date-text">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link 
                        to={`/support/tickets/${ticket.id}`}
                        className="action-btn view"
                        title="View Details"
                      >
                        <FaEye />
                      </Link>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="text-center py-4">No tickets found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {pagination.total > 0 && (
          <Pagination 
            currentPage={pagination.page}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        )}
      </div>
    </div>
  );
};

export default TicketsList;
